import React from 'react';
import { Box, Typography, Chip, Card, CardContent, Stack } from '@mui/material';
import { getPeriodLabel } from '../../utils/calculateMacros';
import { weekplan, grey } from '../../styles/tokens/colors';

/**
 * Labels NAP par valeur numerique
 */
const NAP_LABELS = {
  1.2: 'Sedentaire',
  1.4: 'Peu actif',
  1.5: 'Legerement actif',
  1.6: 'Moderement actif',
  1.7: 'Actif',
  1.8: 'Tres actif',
  1.9: 'Extremement actif',
  2.0: 'Athlete',
};

/**
 * EnergyNeeds - Affichage des besoins energetiques
 *
 * V4.0: Design System Alignment
 * - Utilise les tokens weekplan.dayType pour les couleurs
 * - 3 variantes DEJ (REPOS / SPORT / INTENSE)
 * - Affichage macros P/G/L
 * - Fix bug NAP [object Object]
 */
const EnergyNeeds = ({ user }) => {
  if (!user?.weight || !user?.height || !user?.age || !user?.gender) {
    return (
      <Box sx={{ p: 2, bgcolor: grey[50], borderRadius: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Informations insuffisantes pour calculer les besoins caloriques
        </Typography>
        <Typography variant="caption" color="text.secondary">
          (poids, taille, age, genre requis)
        </Typography>
      </Box>
    );
  }

  const { weight, height, age, gender, sportProfile } = user;

  // Calcul du metabolisme de base (BMR) - Mifflin-St Jeor
  let BMR;
  if (gender === 'male') {
    BMR = 10 * weight + 6.25 * height - 5 * age + 5;
  } else if (gender === 'female') {
    BMR = 10 * weight + 6.25 * height - 5 * age - 161;
  } else {
    const maleBMR = 10 * weight + 6.25 * height - 5 * age + 5;
    const femaleBMR = 10 * weight + 6.25 * height - 5 * age - 161;
    BMR = (maleBMR + femaleBMR) / 2;
  }

  // NAP depuis sportProfile ou valeur par defaut
  const NAP = sportProfile?.nap || 1.5;
  const period = sportProfile?.period || 'base';
  const DEJ_BASE = BMR * NAP;

  // Label NAP (gestion du nombre)
  const napLabel = NAP_LABELS[NAP] || `NAP ${NAP}`;

  // 3 variantes de DEJ selon le type de journee
  const variants = [
    {
      type: 'OFF',
      label: 'Repos',
      calories: Math.round(DEJ_BASE * 0.85),
      styles: weekplan.dayType.OFF,
    },
    {
      type: 'ON',
      label: 'Sport',
      calories: Math.round(DEJ_BASE),
      styles: weekplan.dayType.ON,
    },
    {
      type: 'INTENSIF',
      label: 'Intense',
      calories: Math.round(DEJ_BASE * 1.1),
      styles: weekplan.dayType.INTENSIF,
    },
  ];

  // Calcul des macros pour chaque variante
  const calculateMacros = (calories) => {
    const proteins = Math.round(weight * 2);
    const fats = Math.round(weight * 1);
    const proteinCals = proteins * 4;
    const fatCals = fats * 9;
    const carbCals = calories - proteinCals - fatCals;
    const carbs = Math.round(Math.max(0, carbCals / 4));

    return { proteins, fats, carbs };
  };

  return (
    <Box>
      {/* Header: BMR + NAP + Periode */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Metabolisme de base: <strong>{Math.round(BMR)} kcal</strong>
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip label={napLabel} size="small" variant="outlined" />
          <Chip label={getPeriodLabel(period)} size="small" variant="outlined" />
        </Stack>
      </Stack>

      {/* 3 Cards: REPOS / SPORT / INTENSE */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.5,
        }}
      >
        {variants.map((variant) => {
          const macros = calculateMacros(variant.calories);
          return (
            <Card
              key={variant.type}
              sx={{
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: variant.styles.bg,
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                {/* Label */}
                <Typography
                  variant="overline"
                  sx={{
                    color: variant.styles.text,
                    fontWeight: 600,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {variant.label}
                </Typography>

                {/* Calories */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    color: grey[900],
                  }}
                >
                  {variant.calories}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  kcal
                </Typography>

                {/* Macros */}
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" sx={{ color: grey[600] }}>
                      P: {macros.proteins}g
                    </Typography>
                    <Typography variant="caption" sx={{ color: grey[600] }}>
                      G: {macros.carbs}g
                    </Typography>
                    <Typography variant="caption" sx={{ color: grey[600] }}>
                      L: {macros.fats}g
                    </Typography>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default EnergyNeeds;
